import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Contract } from '@shared/entities/contract';
import { Machine } from '@shared/entities/machine';
import { MachineFilter } from '@shared/entities/machineFilter';
import { Request } from '@shared/entities/request';
import { RequestFilter } from '@shared/entities/requestFilter';
import { Ticket } from '@shared/entities/ticket';
import { TicketFilter } from '@shared/entities/ticketFilter';
import { FilterSelectedEventType } from '@shared/models/types';
import { ContractAlertService } from '@shared/services/api/contracts-alert.service';
import { MachineAlertService } from '@shared/services/api/machine-alert.service';
import { MachineFiltersService } from '@shared/services/api/machine-filters.service';
import { MachineFilterURL, MachineService } from '@shared/services/api/machine.service';
import { RequestInfoFiltersFinalUsersService } from '@shared/services/api/request-info-filters-final-users.service';
import { RequestInfoFinalUsersService } from '@shared/services/api/request-info-final-users.service';
import { RequestFilterURL } from '@shared/services/api/request-info.service';
import { TicketFiltersService } from '@shared/services/api/ticket-filters.service';
import { TicketFilterURL, TicketService } from '@shared/services/api/ticket.service';
import { Observable, combineLatest, of, switchMap, tap } from 'rxjs';
import { ThemeService } from '../services/theme.service';

export interface Alerts {
  hasAlerts: boolean;
  machines: Machine[];
  contracts: Contract[];
}
@Component({
  selector: 'app-final-user-dashboard',
  templateUrl: './final-user-dashboard.component.html',
  styleUrl: './final-user-dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FinalUserDashboardComponent implements OnInit {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _cdr = inject(ChangeDetectorRef);
  private _activatedRoute = inject(ActivatedRoute);
  public translateService = inject(TranslateService);
  private _themeService = inject(ThemeService);
  private _router = inject(Router);
  private _machineService = inject(MachineService);
  private _machineFiltersService = inject(MachineFiltersService);
  private _ticketService = inject(TicketService);
  private _ticketFiltersService = inject(TicketFiltersService);
  private _machineAlertService = inject(MachineAlertService);
  private _contractsAlertService = inject(ContractAlertService);
  private _requestInfoFinalUsersService = inject(RequestInfoFinalUsersService);
  private _requestInfoFiltersService = inject(RequestInfoFiltersFinalUsersService);

  companyName = '';
  activeTab = 'machines';
  isLight = true;
  private _locationId?: string;
  alerts$!: Observable<Alerts>;
  machines$?: Observable<Machine[]>;

  // Machines section
  machineFilters$?: Observable<MachineFilter>;
  prevPageMachines?: number = undefined;
  currentPageMachines?: number = 1;
  nextPageMachines?: number = undefined;

  // Closed Tickets
  closedTickets$!: Observable<Ticket[]>;
  prevPageTicketClosed?: number = undefined;
  currentPageTicketClosed?: number = 1;
  nextPageTicketClosed?: number = undefined;
  totalPagesTicketClosed?: number = undefined;

  // Opened Tickets
  openedTickets$!: Observable<Ticket[]>;
  prevPageTicketOpened?: number = undefined;
  currentPageTicketOpened?: number = 1;
  nextPageTicketOpened?: number = undefined;
  totalPagesTicketOpened?: number = undefined;

  // Request Info
  requestInfo$!: Observable<Request[]>;
  prevPageRequestInfo?: number = undefined;
  currentPageRequestInfo?: number = 1;
  nextPageRequestInfo?: number = undefined;
  requestInfoFilter$!: Observable<RequestFilter>;
  totalPagesRequestInfo?: number = undefined;

  ticketFilter$!: Observable<TicketFilter>;
  isOpen = false;

  constructor() {
    const { locationId, name } = this._activatedRoute.snapshot.queryParams;
    this.companyName = name;
    if (!locationId) {
      this._router.navigate(['/final-users']);
    } else {
      this._locationId = locationId;

      this.machineFilters$ = this._machineFiltersService.all$;
      this.machines$ = this._machineService.all$.pipe(
        switchMap((pagination) => {
          this.prevPageMachines = pagination.prevPage;
          this.currentPageMachines = pagination.currentPage;
          this.nextPageMachines = pagination.nextPage;
          this._cdr.markForCheck();
          return of(pagination.data);
        })
      );

      this.closedTickets$ = this._ticketService.allClosed$.pipe(
        switchMap((pagination) => {
          this.prevPageTicketClosed = pagination.prevPage;
          this.currentPageTicketClosed = pagination.currentPage;
          this.nextPageTicketClosed = pagination.nextPage;
          this.totalPagesTicketClosed = pagination.totalPage;
          this._cdr.markForCheck();
          return of(pagination.data);
        })
      );

      this.openedTickets$ = this._ticketService.allOpened$.pipe(
        switchMap((pagination) => {
          this.prevPageTicketOpened = pagination.prevPage;
          this.currentPageTicketOpened = pagination.currentPage;
          this.nextPageTicketOpened = pagination.nextPage;
          this.totalPagesTicketOpened = pagination.totalPage;
          this._cdr.markForCheck();
          return of(pagination.data);
        })
      );

      this.ticketFilter$ = this._ticketFiltersService.all$;

      // Request info
      this.requestInfoFilter$ = this._requestInfoFiltersService.all$;
      this.requestInfo$ = this._requestInfoFinalUsersService.all$.pipe(
        switchMap((pagination) => {
          this.prevPageRequestInfo = pagination.prevPage;
          this.currentPageRequestInfo = pagination.currentPage;
          this.nextPageRequestInfo = pagination.nextPage;
          this.totalPagesRequestInfo = pagination.totalPage;
          this._cdr.markForCheck();
          return of(pagination.data);
        })
      );

      this.alerts$ = combineLatest([this._machineAlertService.broken$, this._contractsAlertService.lapsed$]).pipe(
        switchMap(([machines, contracts]) => {
          return of({
            hasAlerts: machines.length + contracts.length > 0,
            machines,
            contracts
          });
        })
      );
    }
  }

  ngOnInit() {
    this._themeService.isLight$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((x) => {
          this.isLight = x;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  onSelectedFilter(filters: FilterSelectedEventType[], tableType: 'machines' | 'closed_tickets' | 'requests') {
    const filtersName = filters.reduce((acc: any, filter: FilterSelectedEventType) => {
      acc[filter.name] = filter.selectedValue;
      return acc;
    }, {});
    switch (tableType) {
      case 'machines':
        this._machineFiltersService.filters = filtersName as MachineFilterURL;
        this._machineService.filters = filtersName as MachineFilterURL;
        break;
      case 'closed_tickets':
        this._ticketFiltersService.filters = filtersName as TicketFilterURL;
        this._ticketService.filtersClosed = filtersName as TicketFilterURL;
        break;
      case 'requests':
        this._requestInfoFiltersService.filters = filtersName as RequestFilterURL;
        this._requestInfoFinalUsersService.filters = filtersName as RequestFilterURL;
        break;
      default:
        return;
    }
  }

  onChangePageMachines(page: number) {
    this._machineService.refresh(page);
  }

  onChangePageOpenedTicket(page: number) {
    this._ticketService.refreshOpened(page);
  }

  onChangeClosedTicket(page: number) {
    this._ticketService.refreshClosed(page);
  }

  onChangeRequestInfo(page: number) {
    this._requestInfoFinalUsersService.refresh(page);
  }

  getYear() {
    return new Date().getFullYear();
  }
}
